import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth, db } from '@/lib/db'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { toast } from 'sonner'
import { Loader2, Mail, CheckCircle, ArrowLeft } from 'lucide-react'

export function LoginPage() {
  const auth = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [isSending, setIsSending] = useState(false)
  const [isVerifying, setIsVerifying] = useState(false)
  const [emailSent, setEmailSent] = useState(false)
  const [countdown, setCountdown] = useState(0)

  // 登录成功后的自动跳转逻辑
  useEffect(() => {
    if (auth.user) {
      const from = (location.state as any)?.from || '/'
      navigate(from, { replace: true })
    }
  }, [auth.user, navigate, location.state])

  // 倒计时逻辑
  useEffect(() => {
    let timer: NodeJS.Timeout
    if (countdown > 0) {
      timer = setInterval(() => {
        setCountdown((prev) => prev - 1)
      }, 1000)
    }
    return () => {
      if (timer) clearInterval(timer)
    }
  }, [countdown])

  // 第一步：发送验证码
  const handleSendMagicCode = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!email || !email.includes('@')) {
      toast.error('请输入有效的邮箱地址')
      return
    }

    setIsSending(true)
    try {
      await db.auth.sendMagicCode({ email })
      setEmailSent(true)
      setCountdown(60)
      toast.success('验证码已发送至您的邮箱！')
    } catch (error) {
      console.error('发送验证码失败:', error)
      toast.error('发送失败，请稍后重试')
    } finally {
      setIsSending(false)
    }
  }

  // 第二步：验证验证码并登录
  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault()

    if (code.length !== 6) {
      toast.error('请输入 6 位验证码')
      return
    }

    setIsVerifying(true)
    try {
      await db.auth.signInWithMagicCode({ email, code })
      toast.success('登录成功！')
      // 登录成功后，AuthGuard 会自动处理跳转逻辑
    } catch (error) {
      console.error('验证失败:', error)
      toast.error('验证码错误或已过期，请重试')
    } finally {
      setIsVerifying(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-purple-50 p-4">
      <Card className="w-full max-w-md shadow-xl">
        <CardHeader className="text-center space-y-2 pb-2">
          <div className="mx-auto w-40 mb-4">
            <img src="/logo.png" alt="旋塔.AI" className="w-full h-auto object-contain" />
          </div>
          <CardTitle className="text-2xl font-bold">
            {!emailSent ? '欢迎登录 旋塔.AI' : '输入验证码'}
          </CardTitle>
          <CardDescription>
            专业的小红书笔记 AI 生成工具
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!emailSent ? (
            <form onSubmit={handleSendMagicCode} className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="email" className="text-sm font-medium">
                  电子邮箱
                </label>
                <Input
                  id="email"
                  type="email"
                  placeholder="name@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isSending}
                  className="bg-white"
                  autoFocus
                />
                <p className="text-xs text-muted-foreground">
                  我们将发送 6 位数字验证码到您的邮箱，请注意查收
                </p>
              </div>
              <Button
                type="submit"
                className="w-full h-11 text-base font-semibold"
                disabled={isSending || !email}
              >
                {isSending ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    正在发送...
                  </>
                ) : (
                  <>
                    <Mail className="w-5 h-5 mr-2" />
                    发送验证邮件
                  </>
                )}
              </Button>
            </form>
          ) : (
            <div className="space-y-6">
              <form onSubmit={handleVerifyCode} className="space-y-4">
                <div className="space-y-2">
                  <label htmlFor="code" className="text-sm font-medium text-center block">
                    请输入邮件中的 6 位验证码
                  </label>
                  <Input
                    id="code"
                    type="text"
                    inputMode="numeric"
                    placeholder="000000"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    disabled={isVerifying}
                    className="bg-white text-center text-3xl tracking-[0.6em] font-mono h-14"
                    autoFocus
                  />
                  <p className="text-xs text-center text-muted-foreground">
                    验证码已发送至 <span className="font-medium text-foreground">{email}</span>
                  </p>
                </div>
                <Button
                  type="submit"
                  className="w-full h-11 text-base font-semibold"
                  disabled={isVerifying || code.length !== 6}
                >
                  {isVerifying ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      正在验证...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-5 h-5 mr-2" />
                      立即进入
                    </>
                  )}
                </Button>
              </form>

              <div className="pt-4 border-t space-y-3">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setEmailSent(false)
                    setCode('')
                  }}
                  disabled={isVerifying}
                  className="w-full text-muted-foreground hover:text-foreground"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  返回修改邮箱
                </Button>
                <div className="text-center">
                  <Button
                    variant="link"
                    size="sm"
                    onClick={handleSendMagicCode}
                    disabled={countdown > 0 || isSending || isVerifying}
                    className="text-primary font-medium"
                  >
                    {countdown > 0 ? `${countdown}s 后可重新获取` : '没有收到邮件？点击重新发送'}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
